import { Button, FormField } from '@shared/components/shadcn';
import { Location } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AddressLookupWithConfig } from '../../../../form-controls/AddressLookup/components/AddressLookupWithConfig';
import { useMultistepFormStep } from '../../../../form-controls/MultistepForm/hooks/useMultistepFormStep';
import { TextInput } from '../../../../form-controls/TextInput/TextInput';
import { processAddressFields } from '../../../../Forms/Registration/utils/payloadTransformer';
import { type FormValues, ReKycStepFooter, ReKycStepHeader } from '../../common';

function EditAddressStep() {
  const { handleStepSubmit } = useMultistepFormStep();
  const {
    control,
    getValues,
    setValue,
    formState: { errors: formErrors },
    clearErrors,
  } = useFormContext<FormValues>();

  const t = useTranslations('my_account.rekyc');

  const [isManualEntry, setIsManualEntry] = useState(() => {
    const addressData = getValues('edit-address.address');
    return !!Object.values(addressData).some(Boolean);
  });

  const showManualEntry = () => {
    setIsManualEntry(true);
    setValue('edit-address.addressLookup', '');
    clearErrors();
  };

  const showAddressLookup = () => {
    setIsManualEntry(false);
    setValue('edit-address.addressLookup', '');
    setValue('edit-address.address', {
      streetNr: '',
      streetName: '',
      // addressLine1: '',
      addressLine2: '',
      addressLevel2: '',
      postalCode: '',
    });
    clearErrors();
  };

  return (
    <>
      <ReKycStepHeader
        headerIcon={<Location size="extra-extra-large" data-testid="rekyc-edit-address-icon" />}
        title={t('edit_step.title')}
        description={t('edit_step.description')}
      />
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleStepSubmit} data-testid="edit-address-form">
        <div className="flex flex-col items-stretch gap-4">
          {isManualEntry ? (
            <>
              <FormField
                control={control}
                name="edit-address.address.streetNr"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id={field.name}
                    topActionElement={
                      <Button
                        type="button"
                        variant="ghost"
                        className="link-label-explanatory p-0 h-auto"
                        onClick={showAddressLookup}
                        data-track-name="Address Search"
                        data-testid="address-search-button"
                      >
                        {t('edit_step.search_address')}
                      </Button>
                    }
                    helpMessage={fieldState.error?.message}
                    helpMessageType={fieldState.error ? 'error' : undefined}
                    helperTrackingProps={{ 'data-track-helper': 'House Name/Number' }}
                    label={t('edit_step.street_nr')}
                    type="text"
                    data-track-name="House Name/Number"
                    data-track-privacy="Mask"
                    data-testid="rekyc-street-nr-input"
                  />
                )}
              />
              <FormField
                control={control}
                name="edit-address.address.streetName"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id={field.name}
                    helpMessage={fieldState.error?.message}
                    helpMessageType={fieldState.error ? 'error' : undefined}
                    helperTrackingProps={{ 'data-track-helper': 'Street Name' }}
                    label={t('edit_step.street_name')}
                    type="text"
                    data-track-name="Street Name"
                    data-track-privacy="Mask"
                    data-testid="rekyc-street-name-input"
                  />
                )}
              />
              <FormField
                control={control}
                name="edit-address.address.addressLine2"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id={field.name}
                    helpMessage={fieldState.error?.message}
                    helpMessageType={fieldState.error ? 'error' : undefined}
                    helperTrackingProps={{ 'data-track-helper': 'Address 2' }}
                    label={t('edit_step.address_line2')}
                    type="text"
                    autoComplete="address-line2"
                    data-track-name="Address 2"
                    data-track-privacy="Mask"
                    data-testid="rekyc-address-line2-input"
                  />
                )}
              />
              <div className="flex gap-2">
                <FormField
                  control={control}
                  name="edit-address.address.addressLevel2"
                  render={({ field, fieldState }) => (
                    <TextInput
                      {...field}
                      className="flex-2"
                      id={field.name}
                      helpMessage={fieldState.error?.message}
                      helpMessageType={fieldState.error ? 'error' : undefined}
                      helperTrackingProps={{ 'data-track-helper': 'City' }}
                      label={t('edit_step.address_level2')}
                      type="text"
                      autoComplete="address-level2"
                      data-track-name="City"
                      data-track-privacy="Mask"
                      data-testid="rekyc-address-level2-input"
                    />
                  )}
                />
                <FormField
                  control={control}
                  name="edit-address.address.postalCode"
                  render={({ field, fieldState }) => (
                    <TextInput
                      {...field}
                      className="flex-1"
                      id={field.name}
                      helpMessage={fieldState.error?.message}
                      helpMessageType={fieldState.error ? 'error' : undefined}
                      helperTrackingProps={{ 'data-track-helper': 'Postcode' }}
                      label={t('edit_step.postal_code')}
                      type="text"
                      autoComplete="postal-code"
                      data-track-name="Postcode"
                      data-track-privacy="Mask"
                      data-testid="rekyc-postal-code-input"
                    />
                  )}
                />
              </div>
            </>
          ) : (
            <FormField
              control={control}
              name="edit-address.addressLookup"
              render={({ field }) => (
                <AddressLookupWithConfig
                  {...field}
                  value={field.value ?? ''}
                  id="addressLookup"
                  inputTrackingProps={{
                    'data-track-name': 'Address Finder',
                    'data-track-privacy': 'Mask',
                  }}
                  helpMessage={formErrors?.['edit-address']?.address ? t('edit_step.address_error') : undefined}
                  helpMessageType={formErrors?.['edit-address']?.address ? 'error' : undefined}
                  onAddressSelect={address => {
                    const { streetName, streetNr } = processAddressFields(address);
                    setValue('edit-address.addressLookup', '');
                    setValue('edit-address.address.streetNr', streetNr ?? '');
                    setValue('edit-address.address.streetName', streetName ?? '');
                    // setValue('edit-address.address.addressLine1', address.line1);
                    setValue('edit-address.address.addressLine2', address.line2 ?? '');
                    setValue('edit-address.address.addressLevel2', address.city);
                    setValue('edit-address.address.postalCode', address.postcode);
                    setIsManualEntry(true);
                    clearErrors();
                  }}
                  topActionElement={
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={showManualEntry}
                      className="link-label-explanatory p-0 h-auto"
                      data-track-name="Address Manually"
                      data-testid="address-manually-button"
                    >
                      {t('edit_step.enter_manually')}
                    </Button>
                  }
                  data-track-name="Address Finder"
                  data-track-privacy="Mask"
                />
              )}
            />
          )}
        </div>
        <ReKycStepFooter
          actions={
            <Button type="submit" variant="primary" data-track-name="Continue" data-testid="rekyc-edit-continue-button">
              {t('edit_step.continue')}
            </Button>
          }
        />
      </form>
    </>
  );
}

export { EditAddressStep };
